import { ErrorInfo } from 'react';

import { ActionReducerMapBuilder, Draft, PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { NoInfer } from '@reduxjs/toolkit/src/tsHelpers';

import { ErrorReportBody, ErrorState, ModelError } from 'models';

import {
  FulfilledActionType,
  PendingActionType,
  RejectedActionType,
  errorOr,
  fulfilled,
  pending,
  rejected
} from './storeHelpers';

import GuiServer from 'api/guiServer';

interface ErrorPost {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export const postError = createAsyncThunk<ErrorReportBody, ErrorPost, { rejectValue: ModelError }>(
  'post/error',
  async ({ error, errorInfo }: ErrorPost, { rejectWithValue }) => {
    if (error) {
      const result = await GuiServer.postError(error, errorInfo);
      return errorOr(result, rejectWithValue);
    }
  }
);

const initialState: ErrorState = {
  requestStatus: 'idle',
  requestType: undefined,
  data: undefined,
  error: undefined,
  currentRequestId: undefined,
  globalRequestError: undefined,
  notFound: false,
  offline: false,
  unauthorized: false,
  unprocessableEntity: undefined,
  forbidden: false,
  badRequestError: undefined
};

const errorSlice = createSlice({
  name: 'error',
  initialState,
  reducers: {
    setGlobalRequestError: (state: Draft<ErrorState>, action: PayloadAction<{ error: ModelError }>) => {
      state.globalRequestError = action.payload.error;
    },
    notFound: (state: Draft<ErrorState>, action: PayloadAction<boolean>) => {
      state.notFound = action.payload;
    },
    offline: (state: Draft<ErrorState>) => {
      state.offline = true;
    },
    unauthorized: (state: Draft<ErrorState>) => {
      state.unauthorized = true;
    },
    unprocessableEntity: (
      state: Draft<ErrorState>,
      action: PayloadAction<{ error: { data: ModelError } | ModelError }>
    ) => {
      state.unprocessableEntity = 'data' in action.payload.error ? action.payload.error.data : action.payload.error;
    },
    forbidden: (state: Draft<ErrorState>) => {
      state.forbidden = true;
    },
    badRequest: (state: Draft<ErrorState>, action: PayloadAction<{ error: ModelError }>) => {
      state.badRequestError = action.payload.error;
    },
    clearErrors: (state: Draft<ErrorState>) => {
      state.globalRequestError = undefined;
      state.notFound = false;
      state.offline = false;
      state.unauthorized = false;
      state.forbidden = false;
      state.badRequestError = undefined;
    }
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<ErrorState>>) => {
    builder.addCase(postError.pending, (state: Draft<ErrorState>, action: PendingActionType) => pending(state, action));
    builder.addCase(postError.fulfilled, (state: Draft<ErrorState>, action: FulfilledActionType<ErrorReportBody>) =>
      fulfilled(state, action)
    );
    builder.addCase(postError.rejected, (state: Draft<ErrorState>, action: RejectedActionType) =>
      rejected(state, action)
    );
  }
});

export const {
  notFound,
  offline,
  unauthorized,
  unprocessableEntity,
  setGlobalRequestError,
  forbidden,
  badRequest,
  clearErrors
} = errorSlice.actions;

export default errorSlice.reducer;
